export type BackendErrorResponse = {
  success: false;
  error: {
    code: string;
    http_status: number;
    field?: string | null;
    message: string;
    params: string[];
    trace_id: string;
  };
  timestamp: string;
};

export class ApiHttpError extends Error {
  public readonly status: number;

  public readonly code?: string;

  public readonly field?: string;

  public readonly traceId?: string;

  public readonly params?: string[];

  constructor(
    message: string,
    options: {
      status: number;
      code?: string;
      field?: string;
      traceId?: string;
      params?: string[];
    },
  ) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = options.status;
    this.code = options.code;
    this.field = options.field;
    this.traceId = options.traceId;
    this.params = options.params;
  }
}

const isBackendErrorResponse = (payload: unknown): payload is BackendErrorResponse => {
  if (!payload || typeof payload !== 'object') return false;
  const root = payload as Record<string, unknown>;
  const error = root.error as Record<string, unknown> | undefined;

  return (
    root.success === false &&
    !!error &&
    typeof error.code === 'string' &&
    typeof error.http_status === 'number' &&
    typeof error.message === 'string' &&
    Array.isArray(error.params) &&
    typeof error.trace_id === 'string'
  );
};

export const buildApiHttpError = (
  status: number,
  fallbackMessage: string,
  payload?: unknown,
): ApiHttpError => {
  if (isBackendErrorResponse(payload)) {
    return new ApiHttpError(payload.error.message || fallbackMessage, {
      code: payload.error.code,
      field: payload.error.field ?? undefined,
      params: payload.error.params,
      status,
      traceId: payload.error.trace_id,
    });
  }

  return new ApiHttpError(fallbackMessage, { status });
};

export const getApiErrorMessage = (error: unknown, fallback = 'Something went wrong'): string => {
  if (error instanceof ApiHttpError && error.message) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};
